import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import ProductCard from '@/components/ProductCard';
import axios from 'axios';
import { ArrowRight, Truck, Award, Phone, Wrench, MapPin, Clock, ShieldCheck, Package, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import SEO from '@/components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
  hero_image: 'https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=1200&q=70',
  about_image: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_presentation-site-11/artifacts/sx5g3d10_4.webp',
  cat_ceramice_image: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_presentation-site-11/artifacts/9lmt9sm8_1.webp',
  cat_parchet_image: 'https://images.unsplash.com/photo-1611600700192-d87eaeed4f81?crop=entropy&cs=srgb&fm=jpg&w=600&q=80',
  cat_sanitare_image: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=500&q=70',
  consultation_image: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_presentation-site-11/artifacts/plc5jiei_3.webp',
};

const getImg = (url) => url?.startsWith('/api') ? `${API_URL}${url}` : url;

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cms, setCms] = useState(DEFAULTS);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/products?active_only=true`).then(r => setFeaturedProducts(r.data.slice(0, 4))).catch(() => {}),
      axios.get(`${API_URL}/api/pages/home`).then(r => {
        if (r.data.content && Object.keys(r.data.content).length > 0) setCms(prev => ({ ...prev, ...r.data.content }));
      }).catch(() => {})
    ]).finally(() => setLoading(false));
  }, []);

  const categories = [
    { title: 'Plăci Ceramice', description: 'Gresie și faianță premium din Italia, Spania și India', image: getImg(cms.cat_ceramice_image), href: '/placi-ceramice' },
    { title: 'Parchet', description: 'Laminat Kronotex, SPC și lemn stratificat', image: getImg(cms.cat_parchet_image), href: '/parchet' },
    { title: 'Obiecte Sanitare', description: 'WC, mobilier baie, căzi, cabine duș și baterii', image: getImg(cms.cat_sanitare_image), href: '/obiecte-sanitare' }
  ];

  const trustPoints = [
    { icon: Package, title: 'Orice Produs', desc: 'Aducem orice model din catalogul producătorilor' },
    { icon: Clock, title: 'Termen Scurt', desc: 'Livrare rapidă, fără așteptări lungi' },
    { icon: ShieldCheck, title: 'Cel Mai Bun Preț', desc: 'Prețuri competitive, garantat' },
    { icon: MapPin, title: 'Showroom Brașov', desc: 'Str. Henri Coandă 33A, Brașov' },
  ];

  const services = [
    { icon: Award, title: 'Branduri de Top', description: 'Lucrăm direct cu producători din Italia, Spania, India și Germania. Ai acces la cele mai noi colecții.' },
    { icon: Truck, title: 'Livrare în Toată România', description: 'Comandă de oriunde. Livrăm rapid și în siguranță, direct la ușa ta.' },
    { icon: Wrench, title: 'Consultanță Gratuită', description: 'Te ajutăm să alegi materialele potrivite. Ne deplasăm la fața locului pentru măsurători.' },
    { icon: Star, title: 'Raport Calitate-Preț', description: 'Cele mai bune prețuri din piață, fără compromis la calitate. Cere ofertă personalizată.' }
  ];

  return (
    <Layout>
      <SEO
        title="Gresie, Faianță, Parchet & Obiecte Sanitare Brașov | Shagun Surfaces"
        description="Shagun Surfaces Brașov — gresie, faianță, parchet laminat, obiecte sanitare. Aducem orice produs în cel mai scurt timp, la cel mai bun preț. Showroom Brașov, livrare în toată România."
        canonicalUrl="https://shagunsurfaces.ro/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Shagun Surfaces",
          "description": "Gresie, faianță, parchet laminat și obiecte sanitare premium în Brașov. Aducem orice produs la cel mai bun preț.",
          "url": "https://shagunsurfaces.ro",
          "telephone": "+40761961789",
          "address": { "@type": "PostalAddress", "streetAddress": "Str. Henri Coandă 33A", "addressLocality": "Brașov", "postalCode": "500530", "addressCountry": "RO" },
          "geo": { "@type": "GeoCoordinates", "latitude": "45.6427", "longitude": "25.5887" },
          "openingHours": "Mo-Fr 09:00-18:00, Sa 09:00-14:00",
          "priceRange": "$$"
        }}
      />

      {/* ===== HERO ===== */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${getImg(cms.hero_image)}')` }} />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/85 via-[#050505]/60 to-[#050505]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center pt-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="inline-block mb-4">
              <span className="text-[#D4AF37] text-xs sm:text-sm tracking-[0.3em] uppercase font-medium">Showroom Brașov &bull; Livrare Națională</span>
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight mb-4" data-testid="hero-title">
              SHAGUN<span className="block gold-text">SURFACES</span>
            </h1>
            <div className="w-16 h-[2px] bg-[#D4AF37] mx-auto my-6" />
            <p className="text-white/90 text-lg sm:text-xl max-w-3xl mx-auto mb-3 leading-relaxed font-medium">
              Aducem orice produs, în cel mai scurt timp, la cel mai bun preț.
            </p>
            <p className="text-[#A3A3A3] text-sm sm:text-base max-w-2xl mx-auto mb-10 leading-relaxed">
              Gresie, faianță, parchet laminat, obiecte sanitare și tot ce ai nevoie pentru casa ta. Direct de la producători, cu livrare în toată România.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/placi-ceramice" className="btn-primary inline-flex items-center justify-center gap-2 text-base px-8 py-3.5" data-testid="hero-cta-products">
                Explorează Colecția <ArrowRight size={18} />
              </Link>
              <a href="https://wa.me/40761961789" target="_blank" rel="noopener noreferrer" className="btn-secondary inline-flex items-center justify-center gap-2 text-base px-8 py-3.5" data-testid="hero-cta-contact">
                <Phone size={18} /> Cere Ofertă Rapidă
              </a>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-[#D4AF37] rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-[#D4AF37] rounded-full" />
          </div>
        </div>
      </section>

      {/* ===== TRUST STRIP ===== */}
      <section className="bg-[#0d0d0d] border-y border-[#D4AF37]/20 py-6" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {trustPoints.map((tp, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-10 h-10 flex-shrink-0 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                  <tp.icon size={18} className="text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold leading-tight">{tp.title}</p>
                  <p className="text-[#888] text-xs leading-tight mt-0.5">{tp.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ABOUT / VALUE PROPOSITION ===== */}
      <section className="py-16 sm:py-20 bg-[#0a0a0a]" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase font-medium">Despre noi</span>
              <h2 className="font-serif text-3xl sm:text-4xl text-white mt-3 mb-6">
                Partenerul tău de <span className="gold-text">încredere</span> pentru finisaje
              </h2>
              <div className="space-y-4 text-[#A3A3A3] leading-relaxed">
                <p>
                  <strong className="text-white">Shagun Surfaces</strong> este furnizorul tău complet de materiale de finisaj din <strong className="text-white">Brașov</strong>. De la gresie și faianță premium, la parchet laminat Kronotex și obiecte sanitare moderne — găsești totul într-un singur loc.
                </p>
                <p>
                  Lucrăm direct cu producători din <strong className="text-white">Italia, Spania, India și Germania</strong>, ceea ce ne permite să aducem orice produs din catalogul lor la prețuri mai bune decât oriunde altundeva. Nu contează dacă ai nevoie de un model specific sau de o cantitate mare — noi îl aducem.
                </p>
                <p>
                  Fie că renovezi un apartament, construiești o casă nouă sau amenajezi un spațiu comercial, echipa noastră te ajută cu consultanță gratuită, măsurători la fața locului și livrare rapidă oriunde în România.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/placi-ceramice" className="btn-primary inline-flex items-center gap-2">Vezi Produsele <ArrowRight size={18} /></Link>
                <Link to="/configurator-baie" className="btn-secondary inline-flex items-center gap-2">Configurator Baie 3D <ArrowRight size={18} /></Link>
              </div>
            </div>
            <div className="relative">
              <img src={getImg(cms.about_image)} alt="Showroom Shagun Surfaces Brașov" className="w-full aspect-[4/3] object-cover" loading="lazy" />
              <div className="absolute -bottom-5 -right-5 bg-[#D4AF37] p-5 max-w-[220px] hidden md:block">
                <p className="text-black font-serif text-base font-semibold leading-snug">Orice produs. Cel mai bun preț. Termen scurt.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section className="py-16 sm:py-20 bg-[#050505]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase font-medium">Avantajele noastre</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-white mt-3 mb-4">De Ce Să Alegi Shagun Surfaces</h2>
            <div className="section-divider mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div key={index}
                className="p-7 bg-[#0e0e0e] border border-white/5 hover:border-[#D4AF37]/30 transition-all group">
                <div className="w-12 h-12 mb-5 bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                  <service.icon className="text-[#D4AF37]" size={22} />
                </div>
                <h3 className="font-serif text-lg text-white mb-2">{service.title}</h3>
                <p className="text-[#888] text-sm leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CATEGORIES ===== */}
      <section className="py-16 sm:py-20 bg-[#0a0a0a]" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase font-medium">Produsele noastre</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-white mt-3 mb-4">Categorii Principale</h2>
            <div className="section-divider mx-auto mb-4" />
            <p className="text-[#888] max-w-xl mx-auto text-sm">Gresie, faianță, parchet laminat, obiecte sanitare — totul pentru casa ta, de la producători de renume mondial.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {categories.map((category, index) => (
              <div key={index}>
                <Link to={category.href} className="block group relative overflow-hidden aspect-[4/5]" data-testid={`category-${category.title.toLowerCase().replace(/\s/g, '-')}`}>
                  <img src={category.image} alt={`${category.title} Brașov - Shagun Surfaces`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <h3 className="font-serif text-2xl text-white mb-2">{category.title}</h3>
                    <p className="text-[#A3A3A3] text-sm mb-4">{category.description}</p>
                    <span className="inline-flex items-center gap-2 text-[#D4AF37] font-medium text-sm group-hover:gap-4 transition-all">
                      Vezi Colecția <ArrowRight size={16} />
                    </span>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURED PRODUCTS ===== */}
      {featuredProducts.length > 0 && (
        <section className="py-16 sm:py-20 bg-[#050505]" data-testid="featured-products">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <span className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase font-medium">Din colecție</span>
              <h2 className="font-serif text-3xl sm:text-4xl text-white mt-3 mb-4">Produse Populare</h2>
              <div className="section-divider mx-auto" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredProducts.map((product, index) => (
                <div key={product.id}>
                  <ProductCard product={product} index={index} />
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/placi-ceramice" className="btn-secondary inline-flex items-center gap-2">
                Vezi Toate Produsele <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ===== CTA / CONSULTATION ===== */}
      <section className="py-16 sm:py-20 bg-[#0a0a0a] border-t border-white/5" data-testid="consultation-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase font-medium">Hai să vorbim</span>
              <h2 className="font-serif text-3xl sm:text-4xl text-white mt-3 mb-6">
                Ai un proiect? Te ajutăm de la A la Z.
              </h2>
              <p className="text-[#A3A3A3] leading-relaxed mb-4">
                Indiferent de proiect — renovare apartament, construcție nouă sau amenajare spațiu comercial — echipa Shagun Surfaces din Brașov vine la tine cu consultanță gratuită, măsurători precise și cele mai bune oferte.
              </p>
              <p className="text-[#A3A3A3] leading-relaxed mb-8">
                Sună-ne, scrie-ne pe WhatsApp sau vizitează showroom-ul nostru de pe <strong className="text-white">Str. Henri Coandă 33A, Brașov</strong>.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://wa.me/40761961789" target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="consultation-whatsapp">
                  <Phone size={18} /> WhatsApp: 0761 961 789
                </a>
                <Link to="/contact" className="btn-secondary inline-flex items-center justify-center gap-2">
                  Formular Contact <ArrowRight size={18} />
                </Link>
              </div>
            </div>
            <div className="relative">
              <img src={getImg(cms.consultation_image)} alt="Consultanță gratuită Shagun Surfaces Brașov" className="w-full aspect-[4/3] object-cover" loading="lazy" />
              <div className="absolute -bottom-5 -left-5 bg-[#D4AF37] p-5 max-w-xs hidden md:block">
                <p className="text-black font-serif text-base leading-snug">"Spune-ne ce ai nevoie. Noi ne ocupăm de rest."</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SEO TEXT FOOTER ===== */}
      <section className="py-12 bg-[#050505] border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-[#555] text-xs leading-relaxed text-center">
            Shagun Surfaces — furnizor de gresie, faianță, parchet laminat și obiecte sanitare în Brașov. Oferim plăci ceramice din Italia, Spania și India, parchet Kronotex și SPC, mobilier baie, WC-uri suspendate, căzi, cabine de duș și baterii. Showroom pe Str. Henri Coandă 33A, Brașov. Livrare în toată România. Prețuri competitive pentru proiecte rezidențiale și comerciale.
          </p>
        </div>
      </section>

    </Layout>
  );
};

export default HomePage;
